import * as Sentry from '@sentry/react'
import axios from 'axios'
import { addDays, format, getWeekYear, subDays } from 'date-fns'
import Swal, { type SweetAlertOptions } from 'sweetalert2'

import { SERVICES_URL, WHEELS_URL } from '@/lib/constants'
import type { ApiError } from '@/lib/api/api-error'
import { apiV2Public, getData } from '@/lib/api/client-v2'
import { DATE_ISO } from '@/lib/dates'
import { ENDPOINTS, PUBLIC_ENDPOINTS } from '@/types/api/endpoints'
import type { CompanyV2, CountryV2, GarageV2, TownV2 } from '@/types/api/referential'

/**
 * Format number phone FR xx.xx.xx.xx.xx
 * @param phone
 * @returns {string}
 */
export const formatNumberPhone = (phone?: string | number | null): string => {
    return phone ? (phone.toString().replace(/\./g,'').match(/.{1,2}/g) ?? []).join('.') : ''
}

/**
 * Add space every 3 characters
 * @param number
 * @returns {string}
 */
export const formatThousandSpace = (number: string | number): string => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

/**
 * Format Euro amount with space and €
 * @param number
 * @returns {string}
 */
export const formatEuro = (number: string | number | null | undefined): string => {
    // Montant absent ou illisible : rien plutôt que « NaN € » (`null` reste « 0,00 € »).
    if (number === undefined || Number.isNaN(Number(number))) {
        return ''
    }

    return `${formatThousandSpace(Number(number).toFixed(2)).replace('.', ',')}\u00A0€`
}

/**
 * Normalize input IBAN FR11-2222-3333-4444-5555-6666-777
 * @param value
 * @returns {string}
 */
export const normalizeIban = <T extends string | null | undefined>(value: T): T | string => {
    if (!value) {
        return value
    }

    const separateur = '-'

    const onlyNums = value.replace(/[!@#$%^&*\-(),.?":{}|<>\s]/g, '').toUpperCase()

    if (onlyNums.length <= 4) {
        return onlyNums
    }
    if (onlyNums.length <= 8) {
        return `${onlyNums.slice(0, 4)}${separateur}${onlyNums.slice(4, 8)}`
    }

    if (onlyNums.length <= 12) {
        return `${onlyNums.slice(0, 4)}${separateur}${onlyNums.slice(4, 8)}${separateur}${onlyNums.slice(8, 12)}`
    }

    if (onlyNums.length <= 16) {
        return `${onlyNums.slice(0, 4)}${separateur}${onlyNums.slice(4, 8)}${separateur}${onlyNums.slice(8, 12)}${separateur}${onlyNums.slice(12, 16)}`
    }

    if (onlyNums.length <= 20) {
        return `${onlyNums.slice(0, 4)}${separateur}${onlyNums.slice(4, 8)}${separateur}${onlyNums.slice(8, 12)}${separateur}${onlyNums.slice(12, 16)}${separateur}${onlyNums.slice(16, 20)}`
    }

    if (onlyNums.length <= 24) {
        return `${onlyNums.slice(0, 4)}${separateur}${onlyNums.slice(4, 8)}${separateur}${onlyNums.slice(8, 12)}${separateur}${onlyNums.slice(12, 16)}${separateur}${onlyNums.slice(16, 20)}${separateur}${onlyNums.slice(20, 24)}`
    }

    return `${onlyNums.slice(0, 4)}${separateur}${onlyNums.slice(4, 8)}${separateur}${onlyNums.slice(8, 12)}${separateur}${onlyNums.slice(12, 16)}${separateur}${onlyNums.slice(16, 20)}${separateur}${onlyNums.slice(20, 24)}${separateur}${onlyNums.slice(24, 27)}`
}

/**
 * Normalize input Tel XX.XX.XX.XX.XX
 * @param value
 * @returns {string}
 */
export const normalizeTel = <T extends string | null | undefined>(value: T): T | string => {
    if (!value) {
        return value
    }

    const separateur = '.'

    const onlyNums = value.replace(/[^\d]/g, '')

    if (onlyNums.length <= 2) {
        return onlyNums
    }
    if (onlyNums.length <= 4) {
        return `${onlyNums.slice(0, 2)}${separateur}${onlyNums.slice(2, 4)}`
    }
    if (onlyNums.length <= 6) {
        return `${onlyNums.slice(0, 2)}${separateur}${onlyNums.slice(2, 4)}${separateur}${onlyNums.slice(4, 6)}`
    }
    if (onlyNums.length <= 8) {
        return `${onlyNums.slice(0, 2)}${separateur}${onlyNums.slice(2, 4)}${separateur}${onlyNums.slice(4, 6)}${separateur}${onlyNums.slice(6, 8)}`
    }
    return `${onlyNums.slice(0, 2)}${separateur}${onlyNums.slice(2, 4)}${separateur}${onlyNums.slice(4, 6)}${separateur}${onlyNums.slice(6, 8)}${separateur}${onlyNums.slice(
        8,
        10
    )}`
}

/**
 * Normalize Boolean
 * @param value
 * @returns {boolean}
 */
export const normalizeBooleanForNoobBelair = (value: unknown): 'T' | 'F' => {

    if (value) {
        return "T";
    } else {
        return "F";
    }

};

/** Jours fériés français de l'année `an`, au format `yyyy-MM-dd`. */
export const JoursFeries = (an: number): string[] => {

    const JourAn = `${an}-01-01`
    const FeteTravail = `${an}-05-01`
    const Victoire1945 = `${an}-05-08`
    const FeteNationale = `${an}-07-14`
    const Assomption = `${an}-08-15`
    const Toussaint = `${an}-11-01`
    const Armistice = `${an}-11-11`
    const Noel = `${an}-12-25`
    const SaintEtienne = `${an}-12-26`

    const G = an%19
    const C = Math.floor(an/100)
    const H = (C - Math.floor(C/4) - Math.floor((8*C+13)/25) + 19*G + 15)%30
    const I = H - Math.floor(H/28)*(1 - Math.floor(H/28)*Math.floor(29/(H + 1))*Math.floor((21 - G)/11))
    const J = (an*1 + Math.floor(an/4) + I + 2 - C + Math.floor(C/4))%7
    const L = I - J
    const MoisPaques = 3 + Math.floor((L + 40)/44)
    const JourPaques = L + 28 - 31*Math.floor(MoisPaques/4)
    const PaquesOrigin = new Date(an, MoisPaques - 1, JourPaques)

    const iso = (date: Date) => format(date, DATE_ISO)

    const Paques = iso(PaquesOrigin)
    const VendrediSaint = iso(subDays(PaquesOrigin, 2))
    const LundiPaques = iso(addDays(PaquesOrigin, 1))
    const Ascension = iso(addDays(PaquesOrigin, 39))
    const Pentecote = iso(addDays(PaquesOrigin, 49))
    const LundiPentecote = iso(addDays(PaquesOrigin, 50))

    return [
        JourAn,
        VendrediSaint,
        Paques,
        LundiPaques,
        FeteTravail,
        Victoire1945,
        Ascension,
        Pentecote,
        LundiPentecote,
        FeteNationale,
        Assomption,
        Toussaint,
        Armistice,
        Noel,
        SaintEtienne
    ]

}

/** Année de la semaine courante, comme `moment().weekYear()` en locale française. */
export const currentWeekYear = (): number => getWeekYear(new Date(), { weekStartsOn: 1, firstWeekContainsDate: 4 })

/**
 * Get city by postal code
 * use
 * async fetchCityByPostalCode(value){
 *      let cities = await getCity(value)
 *      console.log(cities)
 * }
 * @param value
 * @returns {string}
 */
export const getCity = <T = string[]>(value: string): Promise<T> => {
    /* v2 publique : même liste de noms de communes que la v1. Un échec laisse la promesse en
       attente, comme avant : l'écran garde ses suggestions. */
    return new Promise(resolve => {
        getData<string[]>(PUBLIC_ENDPOINTS.town(value), undefined, apiV2Public)
            .then(towns => {
                resolve(towns as T)
            }).catch((error: ApiError) => {
            console.log(error)
            if (error.httpStatus !== 404){
                Sentry.captureMessage(`${error.code} PASS - ${error.message}`);
            }
        })
    });
}

/**
 * Get city by search
 * use
 * async fetchCityBySearch(value){
 *      let cities = await getCity(value)
 *      console.log(cities)
 * }
 * @param value
 * @returns {string}
 */
export const getCityBySearch = <T = { codePostal: string; ville: string }[]>(value: string): Promise<T> => {
    /* v2 publique `{postal_code, town}`, rendue sous les noms v1 `{codePostal, ville}` que lisent
       les écrans (et que la déclaration v1 renvoie telle quelle). Un terme que la v2 refuse
       (moins de 3 lettres, chiffres…, 422) donne une liste vide, comme la v1 qui n'y trouvait
       aucune commune. */
    return new Promise(resolve => {
        getData<TownV2[]>(PUBLIC_ENDPOINTS.towns, { search: value }, apiV2Public)
            .then(towns => {
                resolve(towns.map(town => ({ codePostal: town.postal_code, ville: town.town })) as T)
            }).catch((error: ApiError) => {
            console.log(error)
            if (error.httpStatus === 422) {
                resolve([] as T)
            } else if (error.httpStatus !== 404){
                Sentry.captureMessage(`${error.code} PASS - ${error.message}`);
            }
        })
    });
}

/**
 * Get city by value
 * use
 * async fetchCities(value){
 *      let cities = await getCitiesFromFrance(value)
 *      console.log(cities)
 * }
 * @param value
 * @returns [{}]
 */
export const getCitiesFromFrance = <T = unknown>(value: string, request = "SearchBirthplaceWS"): Promise<T> => {
    return new Promise(resolve => {
        axios.get<T>(`${SERVICES_URL}/check-birth?value=${value}&function=${request}`)
            .then(response => {
                resolve(response.data)
            }).catch(error => {
            console.log(error)
        })
    });
}

/**
 * Hides part of the iban FR** 1*** 6*** 1*** 0*** 0**
 * @param value
 * @returns {string}
 */
export const maskIban = <T extends string | null | undefined>(value: T): T | string => {
    if (!value) {
        return value
    }

    return `${value.slice(0, 2)}** ${value.slice(4, 5)}*** ${value.slice(8,9)}*** ${value.slice(12,13)}*** ${value.slice(16,17)}*** ${value.slice(20,21)}*** ${value.slice(24,25)}**`
}

/**
 * Get reparateur by code postal
 * use
 * async fetchReparateurByCodePostal(postalCode){
 *      let reparateurs = await getReparateurByCodePostal(postalCode)
 *      console.log(reparateurs)
 * }
 * @param postalCode
 * @returns {string}
 */
export const getReparateurByCodePostal = <T = unknown>(postalCode: string): Promise<T> => {
    /* La v1 ne servait que 5 chiffres (404 sinon, promesse laissée en attente) ; la v2 répond
       une liste vide, que les écrans prendraient pour « aucun garage ». On garde l'attente. */
    if (!/^[0-9]{5}$/.test(postalCode)) {
        return new Promise(() => undefined)
    }

    /* v2 rendue sous les noms v1 (`raisonSociale`, `rue1`, `datesVisite`…), que lisent l'ajout de
       réparateur et la déclaration de sinistre (dont le corps v1 reprend `id`). La v2 n'expose
       plus `rue2`, `rue3` ni `type`, qu'aucun écran ne lit. */
    return new Promise(resolve => {
        getData<GarageV2[]>(ENDPOINTS.garages(postalCode))
            .then(garages => {
                resolve(garages.map(garage => ({
                    id: garage.id,
                    raisonSociale: garage.name ?? '',
                    rue1: garage.address ?? '',
                    codePostal: garage.postal_code ?? '',
                    ville: garage.city ?? '',
                    phone: garage.phone ?? '',
                    datesVisite: garage.visit_dates,
                })) as T)
            }).catch(error => {
            console.log(error)
        })
    });
}

/**
 * Get city by postal code
 * use
 * async fetchCompagnys(value){
 *      let compagnys = await getCompagnys(value)
 *      console.log(compagnys)
 * }
 * @param value
 * @returns {string}
 */
export const getCompagnys = <T = unknown>(): Promise<T> => {
    /* v2 rendue sous les noms v1 (`adresse`, `codePostal`, `ville`, `codegta`) ; `id` redevient
       une chaîne, valeur que la déclaration v1 envoie et que `codeCompagnys` compare. Les champs
       vides valaient `''` en v1 (et non `null`, qui s'afficherait dans le libellé). */
    return new Promise(resolve => {
        getData<CompanyV2[]>(ENDPOINTS.companies)
            .then(companies => {
                resolve(companies.map(company => ({
                    id: String(company.id),
                    name: company.name,
                    codePostal: company.postal_code ?? '',
                    ville: company.city ?? '',
                    adresse: company.address ?? '',
                    codegta: company.gta_code ?? '',
                })) as T)
            }).catch(error => {
            console.log(error)
        })
    });
}

/**
 * Get city by postal code
 * use
 * async fetchInsurers(value){
 *      let compagnys = await getInsurers(value)
 *      console.log(compagnys)
 * }
 * @param value
 * @returns {string}
 */
export const getCountries = <T = unknown>(): Promise<T> => {
    /* v2 publique `{code, label}` rendue en `{id, name}` v1. Même identifiant des deux côtés
       (`B_TABREF` du jeu Belair `NATIONAL`, rogné), même ordre (France en tête, puis libellé). */
    return new Promise(resolve => {
        getData<CountryV2[]>(PUBLIC_ENDPOINTS.countries, undefined, apiV2Public)
            .then(countries => {
                resolve(countries.map(country => ({ id: country.code, name: country.label })) as T)
            }).catch(error => {
            console.log(error)
        })
    });
}

/**
 * Lighten or Darken hexa color
 * @param hex
 * @param lum
 * @returns {string}
 * @constructor
 */
export const LightenDarkenColor = (hex: string, lum?: number): string => {
    hex = String(hex).replace(/[^0-9a-f]/gi, '')
    if (hex.length < 6) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    }
    lum = lum || 0

    let rgb = '#'
    for (let i = 0; i < 3; i++) {
        const value = parseInt(hex.substring(i * 2, i * 2 + 2), 16)
        const c = Math.round(Math.min(Math.max(0, value + (value * lum)), 255)).toString(16)
        rgb += ('00' + c).substring(c.length)
    }

    return rgb
}


/**
 * Retire les jours feries d'un tableau de date
 * @param array
 * @returns {array}
 * @constructor
 */
export const removeJoursFeries = (array: string[]): string[] => {
    const joursFeries = JoursFeries(currentWeekYear())

    return array.filter(n => !joursFeries.includes(n));
}

/**
 * Get Address
 * use
 * async fetchAddress(value){
 *      let address = await getAddress(value)
 *      console.log(address)
 * }
 * @param value
 * @returns {object}
 */
export const getAddress = <T = unknown>(value: string, validate = 'Undefined', localityId: string | null = null, postalCode: string | null = null, locality: string | null = null): Promise<T> => {
    return new Promise((resolve) => {

        const params = {
            value: encodeURIComponent(value),
            validate,
            localityId,
            postalCode,
            locality
        };

        const filteredParams = Object.entries(params)
            .filter(([, val]) => val !== null)
            .map(([key, val]) => `${key}=${val}`)
            .join('&');

        const url = `${SERVICES_URL}/check-address?${filteredParams}`;

        axios.get<T>(url)
            .then((response) => {
                resolve(response.data);
            })
            .catch((error) => {
                console.log(error);
            });
    });
};

/**
 * Get booking URL for a calendar slug
 * @param slug
 * @param params
 * @returns {Promise<string|null>}
 */
export const getBookingUrl = (slug: string, params: Record<string, string> = {}): Promise<string | null> => {
    // `GET /booking/{slug}` (v2, délégué à la v1) : `{ url }` dans `data`.
    return getData<{ url?: string } | null>(ENDPOINTS.booking(slug), params)
        .then(data => data?.url || null)
        .catch(error => {
            console.log(error)
            return null
        })
}

/**
 * Get Swal config with styled select matching app design
 * @param {object} config - Swal.fire config
 * @returns {object}
 */
export const getSwalSelectConfig = (config: SweetAlertOptions): SweetAlertOptions => {
    const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
    const chevronSvg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 9l-7 7-7-7"/></svg>');

    return {
        confirmButtonColor: primaryColor,
        ...config,
        didOpen: () => {
            const popup = Swal.getPopup();
            if (popup) {
                popup.style.paddingLeft = '1.5rem';
                popup.style.paddingRight = '1.5rem';
            }
            const select = Swal.getInput();
            if (select) {
                Object.assign(select.style, {
                    width: '100%',
                    padding: '0.75rem 2.5rem 0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-card)',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    transition: 'all 150ms',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer',
                    outline: 'none',
                    margin: '1rem auto 0',
                    backgroundImage: `url("data:image/svg+xml,${chevronSvg}")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1rem',
                });
                select.addEventListener('focus', () => {
                    select.style.boxShadow = `0 0 0 2px color-mix(in oklch, ${primaryColor} 20%, transparent)`;
                    select.style.borderColor = primaryColor;
                });
                select.addEventListener('blur', () => {
                    select.style.boxShadow = 'none';
                    select.style.borderColor = 'var(--color-border)';
                });
            }
            if (config.didOpen) (config.didOpen as () => void)();
        },
    };
};

export const isMobile = (): boolean => {
    return !!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}



export const formatImmatriculation = <T extends string | null | undefined>(value: T): T | string => {
    if (!value) {
        return value
    }

    const separateur = '-'

    const onlyNums = value.replace(/[!@#$%^&*\-(),.?":{}|<> ]/g, '').toUpperCase()

    // Nouvelle plaque AA123AA, AA123A ou C254L ; ancienne 1234AA12 ; ancienne corse 1234AA2A.
    const plate =
        /^([A-Z]{1,2})(\d{2,3})([A-Z]{1,2})$/.exec(onlyNums) ??
        /^(\d{1,4})([A-Z]{2,3})(\d{2})$/.exec(onlyNums) ??
        /^(\d{1,4})([A-Z]{2,3})(\d[AB])$/.exec(onlyNums)

    if (plate) {
        return plate.slice(1).join(separateur)
    }

    return value.slice(0, 11)

}

export const getTypesByWheels = <T = unknown>(): Promise<T[]> => {
    return new Promise(resolve => {
        axios.get<{ data?: T[] }>(`${WHEELS_URL}/types`, { params: { _: Date.now() } })
            .then(response => resolve(response?.data?.data ?? []))
            .catch(error => { console.log(error); resolve([]) })
    })
}

export const getBrandsByTypes = <T = unknown>({type}: {type: string}): Promise<T[]> => {
    return new Promise(resolve => {
        axios.get<{ data: T[] }>(`${WHEELS_URL}/brands?type=${type}`)
            .then(response => resolve(response.data.data))
            .catch(error => { console.log(error); resolve([]) })
    })
}

export const getSizesByWheels = <T = unknown>({type, brand}: {type: string, brand: string}): Promise<T[]> => {
    return new Promise(resolve => {
        axios.get<{ data: T[] }>(`${WHEELS_URL}/sizes?type=${type}&brand=${brand}`)
            .then(response => resolve(response.data.data))
            .catch(error => { console.log(error); resolve([]) })
    })
}

export const getVehiculesByWheels = <T = unknown>({type, brand, size}: {type: string, brand: string, size: string}): Promise<T[]> => {
    return new Promise(resolve => {
        axios.get<{ data: T[] }>(`${WHEELS_URL}/vehicles?type=${type}&brand=${brand}&size=${size}`)
            .then(response => resolve(response.data.data))
            .catch(error => { console.log(error); resolve([]) })
    })
}

/**
 * Style les liens seuls dans leur paragraphe (contenu HTML du CMS) en CTA
 * @param html
 * @returns {string}
 */
export const styleStandaloneLinksAsCta = <T extends string | null | undefined>(html: T): T | string => {
    if (!html) {
        return html
    }

    const doc = new DOMParser().parseFromString(html, 'text/html')

    doc.querySelectorAll('p > a').forEach(link => {
        const paragraph = link.parentElement

        if (paragraph && paragraph.children.length === 1 && paragraph.textContent?.trim() === link.textContent?.trim()) {
            // Équivalent Tailwind des anciennes classes `btn btn-primary` et `text-center mt-3`.
            link.classList.add(
                'inline-flex', 'items-center', 'justify-center', 'gap-2', 'px-5', 'py-2.5', 'text-[0.875rem]/5',
                'font-medium', 'rounded-md', 'transition-all', 'ease-[ease]', 'cursor-pointer', 'no-underline',
                'bg-primary', 'text-primary-foreground', 'border-none', 'hover:bg-primary-hover',
            )
            paragraph.classList.add('text-center', 'mt-4')
        }
    })

    return doc.body.innerHTML
}
